import { computed, observable, makeAutoObservable } from 'mobx';
import { OpenapiProxyStaffRule4UpdateParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_rule4_update';
import { AppState } from 'src/AppState/AppState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { FormConcept } from 'src/components/EventResultsSettings/FormConcept';

interface Rule4ModelType {
    id: number;
    endTime: string | null | undefined;
    selection: number | null | undefined;
    deduction: number | null | undefined;
}

type SelectionSortType = {
    id: number;
    name: string;
};

class MarketView {
    public constructor(public readonly marketId: MarketId) {}

    public getMarketModel(): MarketModel | null {
        return this.marketId.getModel();
    }

    public list = (): Array<number> => {
        const marketModel = this.getMarketModel();

        if (marketModel === null) {
            return [];
        }

        const list = [];
        for (const rule4Item of marketModel.getRawData().rule4 ?? []) {
            list.push(rule4Item.id);
        }

        list.sort((a, b): number => {
            return a - b;
        });

        return list;
    };

    public getSelections = (): Array<SelectionSortType> => {
        const marketModel = this.marketId.getModel();

        if (marketModel === null) {
            return [];
        }

        const result: Array<SelectionSortType> = [];

        for (const selection of marketModel.selections) {
            // if (
            //     selection.templateId !== 'unnamed-favourite' &&
            //     selection.metaDataStatus !== 'Non-Runner'
            // ) {
            //     result.push({
            //         id: selection.id,
            //         name: selection.name,
            //     });
            // }

            result.push({
                id: selection.id,
                name: selection.name,
            });

            // .filter(x => x.getIn([ 'template', 'id' ]) !== 'unnamed-favourite')
            // .filter(x => x.getIn([ 'metadata', 'status' ]) !== 'Non-Runner');
        }

        return result;
    };
}

class NewModel {
    @observable private newModelId: null | number = null;

    public constructor(private readonly marketView: MarketView) {
        makeAutoObservable(this);
    }

    @computed public get newId(): null | number {
        const newModelId = this.newModelId;

        if (newModelId === null) {
            return null;
        }

        const list = this.marketView.list();

        if (list.includes(newModelId)) {
            return null;
        }

        return newModelId;
    }

    private getNextId(): number {
        const list = this.marketView.list();
        const last = list[list.length - 1];

        if (last !== undefined) {
            return last + 1;
        }

        return 1;
    }

    public add = (): void => {
        this.newModelId = this.getNextId();
    };
}

export class ModelRule4 {
    @observable public inProgress: boolean = false;
    private inputEndTime: FormConcept<string | null>;
    private inputSelection: FormConcept<number | null>;
    private inputDeduction: FormConcept<number | null>;

    // private inProgress: boolean = false;

    public constructor(
        private readonly appState: AppState,
        private readonly eventId: EventId,
        public readonly id: number,
        private readonly getModel: () => Rule4ModelType | null,
        private readonly marketView: MarketView,
        private readonly newModel: NewModel
    ) {
        this.inputEndTime = new FormConcept((): string | null => {
            return this.getModel()?.endTime ?? null;
        });

        this.inputSelection = new FormConcept(() => {
            return this.getModel()?.selection ?? null;
        });

        this.inputDeduction = new FormConcept(() => {
            return this.getModel()?.deduction ?? null;
        });
        makeAutoObservable(this);
    }

    @computed public get valueEndTime(): string {
        return this.inputEndTime.value ?? '';
    }

    @computed public get valueWinner(): string {
        return this.inputSelection.value?.toString() ?? '';
    }

    @computed public get valueDeduction(): string {
        return this.inputDeduction.value?.toString() ?? '';
    }

    public setEndTime = (valueText: string | null): void => {
        this.inputEndTime.setValue(valueText ?? '');
    };

    public setSelection = (valueText: string): void => {
        const value = parseInt(valueText, 10);
        if (isNaN(value)) {
            console.error('setSelection: expect number');
        } else {
            this.inputSelection.setValue(value);
        }
    };

    public setDeduction = (valueText: string): void => {
        const value = parseInt(valueText, 10);
        if (isNaN(value)) {
            console.error('set2nd: expect number');
        } else {
            this.inputDeduction.setValue(value);
        }
    };

    @computed public get selections(): Array<SelectionSortType> {
        return this.marketView.getSelections();
    }

    @computed private get isNew(): boolean {
        if (this.newModel.newId === this.id) {
            return true;
        }

        return false;
    }

    @computed public get buttonLabel(): string {
        return this.isNew ? 'Add' : 'Save';
    }

    private getData = (): OpenapiProxyStaffRule4UpdateParamsType['requestBody'] | string => {
        const valueEndTime = this.inputEndTime.value;

        if (valueEndTime === null) {
            return 'field "endTime" - No selected date';
        }

        const valueSelection = this.inputSelection.value;

        if (valueSelection === null) {
            return 'field "Runner" - No selected';
        }

        const valueDeduction = this.inputDeduction.value;

        if (valueDeduction === null) {
            return 'field "Deduction" - No selected';
        }

        return {
            endTime: valueEndTime,
            selection: valueSelection,
            deduction: valueDeduction,
        };
    };

    //

    public get isChange(): boolean {
        if (this.isNew) {
            return true;
        } else {
            return this.inputEndTime.wasChange || this.inputSelection.wasChange || this.inputDeduction.wasChange;
        }
    }

    private resetInputs = (): void => {
        this.inputEndTime.reset();
        this.inputSelection.reset();
        this.inputDeduction.reset();
    };

    public saveOrAdd = async (): Promise<void> => {
        if (this.inProgress) {
            console.warn('saveOrAdd - ignore action - request currently in progress');
            return;
        }

        const data = this.getData();

        if (typeof data === 'string') {
            this.appState.reduxAction.addError(data);
            return;
        }

        this.inProgress = true;

        try {
            if (this.isNew) {
                const response = await this.appState.sdkStaff.staff_selection_rule4_add({
                    event_id: this.eventId.toOldId(),
                    market_id: this.marketView.marketId.toOldId(),
                    requestBody: data,
                });

                if (this.appState.reduxAction.addErrorEventChange(response)) {
                    this.inProgress = false;
                    return;
                }
            } else {
                const response = await this.appState.sdkStaff.staff_selection_rule4_update({
                    event_id: this.eventId.toOldId(),
                    market_id: this.marketView.marketId.toOldId(),
                    id: this.id,
                    requestBody: data,
                });

                if (this.appState.reduxAction.addErrorEventChange(response)) {
                    this.inProgress = false;
                    return;
                }
            }

            await this.appState.sdkStaff.triggerUpdateMarket(this.marketView.marketId);
        } catch (error) {
            console.error(error);
            this.inProgress = false;
            return;
        }

        await timeout(1500);

        this.inProgress = false;
        this.resetInputs();
    };
}

export const groupRule4Model = (marketId: MarketId, id: number): Rule4ModelType | null => {
    const marketModel = marketId.getModel();

    if (marketModel === null) {
        return null;
    }

    for (const rule4Item of marketModel.getRawData().rule4 ?? []) {
        if (rule4Item.id === id) {
            return rule4Item;
        }
    }

    return null;
};

export class Rule4State {
    private readonly models: MobxMapAutoNew<number, ModelRule4>;

    private readonly marketView: MarketView;
    private readonly newModel: NewModel;

    public constructor(
        private readonly appState: AppState,
        private readonly eventId: EventId,
        private readonly marketId: MarketId
    ) {
        this.marketView = new MarketView(marketId);

        this.newModel = new NewModel(this.marketView);

        this.models = new MobxMapAutoNew((id: number): ModelRule4 => {
            const getModel = (): Rule4ModelType | null => groupRule4Model(this.marketId, id);

            return new ModelRule4(this.appState, this.eventId, id, getModel, this.marketView, this.newModel);
        });
    }

    public listForView(): Array<ModelRule4> {
        const results = this.marketView.list();

        const newModelId = this.newModel.newId;

        if (newModelId !== null) {
            results.push(newModelId);
        }

        return results.map((id) => this.models.get(id));
    }

    public add = (): void => {
        this.newModel.add();
    };
}
